/**
 * Profile Photo Modal Component
 * View current photo; remove or change via file picker and square crop
 * PATCH api/v2/artist/profile-pic/ then refetches artist details
 *
 * @author Varun Pandey
 */

import React, { useState, useRef, useCallback, useLayoutEffect } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import {
  ReactCrop,
  type PercentCrop,
  centerCrop,
  makeAspectCrop,
  convertToPixelCrop,
  convertToPercentCrop,
} from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { X, User, ImagePlus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { cn } from '@/utils/cn';
import { getCroppedImg } from '@/utils/cropImage';
import { ArtistService } from '@/services/artistService';
import { useArtistDetailsStore } from '@/stores/artistDetailsStore';
import toast from 'react-hot-toast';

export interface ProfilePhotoModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentPic: string | null | undefined;
  userName?: string;
}

/** Percents are relative to the crop box; force equal pixel width/height (1:1). */
function toSquarePercentCrop(
  percent: PercentCrop,
  boxWidth: number,
  boxHeight: number
): PercentCrop {
  const px = convertToPixelCrop(percent, boxWidth, boxHeight);
  let side = Math.min(px.width, px.height);
  side = Math.max(1, Math.min(side, boxWidth, boxHeight));
  let x = px.x + (px.width - side) / 2;
  let y = px.y + (px.height - side) / 2;
  x = Math.max(0, Math.min(x, boxWidth - side));
  y = Math.max(0, Math.min(y, boxHeight - side));
  return convertToPercentCrop(
    { unit: 'px', x, y, width: side, height: side },
    boxWidth,
    boxHeight
  );
}

function initialSquareCropInRect(mediaW: number, mediaH: number): PercentCrop {
  return centerCrop(
    makeAspectCrop({ unit: '%' as const, width: 85 }, 1, mediaW, mediaH),
    mediaW,
    mediaH
  );
}

export const ProfilePhotoModal: React.FC<ProfilePhotoModalProps> = ({
  open,
  onOpenChange,
  currentPic,
  userName = 'Profile',
}) => {
  const [step, setStep] = useState<'view' | 'crop'>('view');
  const [selectedFile, setSelectedFile] = useState<string | null>(null);
  const [crop, setCrop] = useState<PercentCrop | undefined>(undefined);
  const [isUpdating, setIsUpdating] = useState(false);
  const [naturalSize, setNaturalSize] = useState<{ w: number; h: number } | null>(
    null
  );
  const [mediaDisplay, setMediaDisplay] = useState<{ w: number; h: number } | null>(
    null
  );
  const imgRef = useRef<HTMLImageElement>(null);
  const cropViewportRef = useRef<HTMLDivElement>(null);
  const cropMediaRef = useRef<HTMLDivElement>(null);
  const lastMediaLayoutRef = useRef({ w: 0, h: 0 });
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { fetchArtistDetails } = useArtistDetailsStore();

  const resetToView = useCallback(() => {
    setStep('view');
    setSelectedFile(null);
    setCrop(undefined);
    setNaturalSize(null);
    setMediaDisplay(null);
    lastMediaLayoutRef.current = { w: 0, h: 0 };
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  const handleClose = useCallback(() => {
    if (selectedFile) URL.revokeObjectURL(selectedFile);
    onOpenChange(false);
    resetToView();
  }, [onOpenChange, resetToView, selectedFile]);

  const handleChangePhoto = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith('image/')) return;
    const url = URL.createObjectURL(file);
    setSelectedFile(url);
    setCrop(undefined);
    setNaturalSize(null);
    setMediaDisplay(null);
    lastMediaLayoutRef.current = { w: 0, h: 0 };
    setStep('crop');
    onOpenChange(true);
  };

  const onImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    if (img.naturalWidth < 1 || img.naturalHeight < 1) return;
    setNaturalSize({ w: img.naturalWidth, h: img.naturalHeight });
  };

  useLayoutEffect(() => {
    if (step !== 'crop' || !selectedFile || !naturalSize) return;
    const el = cropViewportRef.current;
    if (!el) return;
    lastMediaLayoutRef.current = { w: 0, h: 0 };

    const run = () => {
      const cw = el.clientWidth;
      const ch = el.clientHeight;
      if (cw < 2 || ch < 2) return;
      const scale = Math.min(cw / naturalSize.w, ch / naturalSize.h);
      const w = naturalSize.w * scale;
      const h = naturalSize.h * scale;
      const last = lastMediaLayoutRef.current;
      if (Math.abs(w - last.w) < 0.5 && Math.abs(h - last.h) < 0.5) {
        return;
      }
      lastMediaLayoutRef.current = { w, h };
      setMediaDisplay({ w, h });
      setCrop((prev) =>
        prev
          ? toSquarePercentCrop(prev, w, h)
          : initialSquareCropInRect(w, h)
      );
    };

    run();
    const ro = new ResizeObserver(run);
    ro.observe(el);
    return () => ro.disconnect();
  }, [step, selectedFile, naturalSize]);

  const handleCropChange = useCallback((_: unknown, percentCrop: PercentCrop) => {
    const box = cropMediaRef.current;
    if (!box) {
      setCrop(percentCrop);
      return;
    }
    const cw = box.clientWidth;
    const ch = box.clientHeight;
    if (cw < 2 || ch < 2) {
      setCrop(percentCrop);
      return;
    }
    setCrop(toSquarePercentCrop(percentCrop, cw, ch));
  }, []);

  const handleUpdateCrop = async () => {
    if (!imgRef.current || !crop) return;
    setIsUpdating(true);
    try {
      const file = await getCroppedImg(imgRef.current, {
        x: crop.x,
        y: crop.y,
        width: crop.width,
        height: crop.height,
      });
      await ArtistService.updateProfilePic(file);
      await fetchArtistDetails();
      toast.success('Profile picture updated');
      handleClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update photo');
    } finally {
      setIsUpdating(false);
    }
  };



  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFileSelect}
      />
      <Dialog.Root open={open} onOpenChange={(o) => !o && handleClose()}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-50 bg-black/70 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
            <Dialog.Content
              className={cn(
                'w-[min(90vw,400px)] max-h-[90vh] flex flex-col pointer-events-auto',
                'rounded-2xl bg-[linear-gradient(135deg,_#000000_0%,_#0c0c0e_40%,_#16161a_100%)] border border-[#3A3A3C]/50 shadow-2xl overflow-hidden',
                'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0'
              )}
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <Dialog.Title className="text-lg font-bold text-white">
                    {step === 'view' ? 'Profile photo' : 'Crop photo'}
                  </Dialog.Title>
                  <Dialog.Close asChild>
                    <button
                      type="button"
                      className="rounded-lg p-1.5 text-[#8E8E93] hover:text-white hover:bg-white/10"
                      aria-label="Close"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </Dialog.Close>
                </div>

                {step === 'view' && (
                  <>
                    <div className="flex justify-center mb-6">
                      <div className="w-40 h-40 rounded-full overflow-hidden border-2 border-[#3A3A3C] bg-[#2C2C2E] flex items-center justify-center">
                        {currentPic ? (
                          <img
                            src={currentPic}
                            alt={userName}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <User className="h-16 w-16 text-[#8E8E93] opacity-50" />
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col gap-2">
                      <Button
                        variant="secondary"
                        className="w-full"
                        onClick={handleChangePhoto}
                      >
                        <ImagePlus className="h-4 w-4 mr-2" />
                        {currentPic ? 'Update photo' : 'Upload photo'}
                      </Button>
                    </div>
                  </>
                )}

                {step === 'crop' && selectedFile && (
                  <>
                    {/* Square frame; ReactCrop only wraps the object-contain-sized image so the selector cannot sit in letterbox. */}
                    <div
                      ref={cropViewportRef}
                      className="relative mx-auto flex w-full max-w-[280px] aspect-square shrink-0 items-center justify-center overflow-hidden rounded-xl bg-[#0B0B0D] sm:max-w-[320px]"
                    >
                      {/*
                        Always mount <img> so onLoad can set naturalSize. Gating on mediaDisplay caused a deadlock (no img → no naturalSize → no mediaDisplay).
                      */}
                      <div
                        ref={cropMediaRef}
                        className={cn(
                          'relative min-h-0 min-w-0 shrink-0',
                          !mediaDisplay ||
                            mediaDisplay.w <= 1 ||
                            mediaDisplay.h <= 1
                            ? 'h-full w-full max-h-full max-w-full'
                            : ''
                        )}
                        style={
                          mediaDisplay &&
                            mediaDisplay.w > 1 &&
                            mediaDisplay.h > 1
                            ? {
                              width: mediaDisplay.w,
                              height: mediaDisplay.h,
                            }
                            : undefined
                        }
                      >
                        <ReactCrop
                          crop={crop}
                          onChange={handleCropChange}
                          aspect={1}
                          className={cn(
                            'absolute inset-0 !block min-h-0 min-w-0 overflow-hidden',
                            '[&_.ReactCrop__child-wrapper]:!absolute [&_.ReactCrop__child-wrapper]:!inset-0 [&_.ReactCrop__child-wrapper]:!block [&_.ReactCrop__child-wrapper]:!min-h-0 [&_.ReactCrop__child-wrapper]:!min-w-0',
                            '[&_.ReactCrop__child-wrapper>img]:!block [&_.ReactCrop__child-wrapper>img]:!h-full [&_.ReactCrop__child-wrapper>img]:!w-full [&_.ReactCrop__child-wrapper>img]:!object-cover'
                          )}
                        >
                          <img
                            ref={imgRef}
                            src={selectedFile}
                            alt="Crop"
                            className="block h-full w-full object-contain"
                            onLoad={onImageLoad}
                          />
                        </ReactCrop>
                      </div>
                    </div>
                    <div className="mt-4 flex justify-end shrink-0">
                      <Button
                        onClick={handleUpdateCrop}
                        disabled={!crop || isUpdating}
                      >
                        {isUpdating ? 'Updating…' : 'Update'}
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </Dialog.Content>
          </div>
        </Dialog.Portal>
      </Dialog.Root>
    </>
  );
};
